import { jsx as _jsx } from "react/jsx-runtime";
// ============================================
// Auth Provider for Parent Web
// ============================================
import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
const AuthContext = createContext(undefined);
function decodeJwtPayload(token) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''));
    return JSON.parse(jsonPayload);
}
function extractUserFromToken(token) {
    try {
        const payload = decodeJwtPayload(token);
        const realmRoles = payload.realm_access?.roles ?? [];
        let role = 'PARENT';
        if (realmRoles.includes('ADMIN'))
            role = 'ADMIN';
        else if (realmRoles.includes('CHILD'))
            role = 'CHILD';
        const email = payload.email ?? payload.preferred_username ?? '';
        const firstName = payload.given_name ?? '';
        const lastName = payload.family_name ?? '';
        const name = [firstName, lastName].filter(Boolean).join(' ')
            || payload.name
            || payload.preferred_username
            || email.split('@')[0]
            || '';
        return { id: payload.sub, email, name, firstName, lastName, role };
    }
    catch {
        return null;
    }
}
function isTokenExpired(token) {
    try {
        const payload = decodeJwtPayload(token);
        return Date.now() >= payload.exp * 1000;
    }
    catch {
        return true;
    }
}
function clearSession() {
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('id_token');
}
async function tryRefreshToken(keycloakUrl, realm, clientId) {
    const refreshToken = sessionStorage.getItem('refresh_token');
    if (!refreshToken)
        return null;
    try {
        const res = await fetch(`${keycloakUrl}/realms/${realm}/protocol/openid-connect/token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
                grant_type: 'refresh_token',
                client_id: clientId,
                refresh_token: refreshToken,
            }).toString(),
        });
        if (!res.ok)
            return null;
        const data = await res.json();
        sessionStorage.setItem('access_token', data.access_token);
        if (data.refresh_token)
            sessionStorage.setItem('refresh_token', data.refresh_token);
        if (data.id_token)
            sessionStorage.setItem('id_token', data.id_token);
        return data.access_token;
    }
    catch {
        return null;
    }
}
const API_URL = import.meta.env.VITE_API_URL || '';
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const keycloakUrl = import.meta.env.VITE_KEYCLOAK_URL ?? 'http://localhost:8180';
    const realm = 'parental-control';
    const clientId = 'parent-web';
    const getAccessToken = useCallback(() => {
        return sessionStorage.getItem('access_token');
    }, []);
    /** Recharge le profil depuis /api/users/me et met à jour le state */
    const refreshProfile = useCallback(async () => {
        const token = sessionStorage.getItem('access_token');
        if (!token)
            return;
        try {
            const res = await fetch(`${API_URL}/api/users/me`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok)
                return;
            const json = await res.json();
            const data = json.data;
            if (!data)
                return;
            setUser(prev => prev ? {
                ...prev,
                firstName: data.firstName || prev.firstName,
                lastName: data.lastName || prev.lastName,
                name: [data.firstName || '', data.lastName || ''].filter(Boolean).join(' ') || prev.name,
                email: data.email || prev.email,
                createdAt: data.createdAt ?? prev.createdAt,
            } : prev);
        }
        catch {
            // silently ignore
        }
    }, []);
    // ── Initialisation auth (une seule fois au montage) ──────────────────────
    useEffect(() => {
        const initAuth = async () => {
            try {
                const pathname = window.location.pathname;
                const search = window.location.search;
                const base = import.meta.env.BASE_URL; // '/app/'
                if (pathname === `${base}auth/callback`) {
                    // Échange du code d'autorisation contre les tokens
                    const params = new URLSearchParams(search);
                    const code = params.get('code');
                    if (!code) {
                        navigate('/login', { replace: true });
                        return;
                    }
                    const redirectUri = `${window.location.origin}${base}auth/callback`;
                    const res = await fetch(`${keycloakUrl}/realms/${realm}/protocol/openid-connect/token`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                        body: new URLSearchParams({
                            grant_type: 'authorization_code',
                            client_id: clientId,
                            code,
                            redirect_uri: redirectUri,
                        }).toString(),
                    });
                    if (res.ok) {
                        const data = await res.json();
                        sessionStorage.setItem('access_token', data.access_token);
                        if (data.refresh_token)
                            sessionStorage.setItem('refresh_token', data.refresh_token);
                        if (data.id_token)
                            sessionStorage.setItem('id_token', data.id_token);
                        const extractedUser = extractUserFromToken(data.access_token);
                        if (extractedUser) {
                            setUser(extractedUser);
                            // Retourner à la page intentée avant le login
                            const redirect = sessionStorage.getItem('login_redirect') || '/dashboard';
                            sessionStorage.removeItem('login_redirect');
                            navigate(redirect, { replace: true });
                        }
                        else {
                            navigate('/login', { replace: true });
                        }
                    }
                    else {
                        console.error('Token exchange failed:', res.status);
                        navigate('/login', { replace: true });
                    }
                }
                else {
                    // Vérifier le token existant (avec tentative de refresh si expiré)
                    const token = sessionStorage.getItem('access_token');
                    if (token && !isTokenExpired(token)) {
                        setUser(extractUserFromToken(token));
                    }
                    else if (token || sessionStorage.getItem('refresh_token')) {
                        const newToken = await tryRefreshToken(keycloakUrl, realm, clientId);
                        if (newToken) {
                            setUser(extractUserFromToken(newToken));
                        }
                        else {
                            clearSession();
                            setUser(null);
                        }
                    }
                }
            }
            catch (error) {
                console.error('Auth init failed:', error);
                setUser(null);
            }
            finally {
                setIsLoading(false);
            }
        };
        initAuth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // ── Refresh proactif : renouveler 1 minute avant expiration ─────────────
    useEffect(() => {
        if (!user)
            return;
        const token = sessionStorage.getItem('access_token');
        if (!token)
            return;
        let exp;
        try {
            exp = decodeJwtPayload(token).exp * 1000;
        }
        catch {
            return;
        }
        const delay = exp - Date.now() - 60000; // 1 min avant expiration
        const doRefresh = async () => {
            const newToken = await tryRefreshToken(keycloakUrl, realm, clientId);
            if (newToken) {
                const refreshed = extractUserFromToken(newToken);
                // Préserver createdAt (non présent dans le JWT) lors du refresh proactif
                setUser(prev => refreshed ? { ...refreshed, createdAt: prev?.createdAt } : null);
            }
            else {
                clearSession();
                setUser(null);
            }
        };
        if (delay <= 0) {
            doRefresh();
            return;
        }
        const timer = setTimeout(doRefresh, delay);
        return () => clearTimeout(timer);
    }, [user, keycloakUrl]);
    const login = useCallback(async () => {
        // Sauvegarder la page intentée pour y revenir après login (chemin relatif à la base)
        const base = import.meta.env.BASE_URL; // '/app/'
        const baseNoSlash = base.replace(/\/$/, ''); // '/app'
        const currentPath = window.location.pathname + window.location.search;
        if (currentPath !== `${baseNoSlash}/login` && currentPath !== `${baseNoSlash}/auth/callback`) {
            // Stocker le chemin relatif au basename pour que navigate() fonctionne correctement
            const relativePath = currentPath.startsWith(baseNoSlash)
                ? currentPath.slice(baseNoSlash.length) || '/'
                : currentPath;
            sessionStorage.setItem('login_redirect', relativePath);
        }
        const redirectUri = `${window.location.origin}${base}auth/callback`;
        const authUrl = new URL(`${keycloakUrl}/realms/${realm}/protocol/openid-connect/auth`);
        authUrl.searchParams.set('client_id', clientId);
        authUrl.searchParams.set('redirect_uri', redirectUri);
        authUrl.searchParams.set('response_type', 'code');
        authUrl.searchParams.set('scope', 'openid profile email');
        window.location.href = authUrl.toString();
    }, [keycloakUrl]);
    const logout = useCallback(async () => {
        const idToken = sessionStorage.getItem('id_token');
        clearSession();
        setUser(null);
        const logoutUrl = `${keycloakUrl}/realms/${realm}/protocol/openid-connect/logout`;
        const params = new URLSearchParams({
            client_id: clientId,
            post_logout_redirect_uri: `${window.location.origin}${import.meta.env.BASE_URL}login`,
        });
        if (idToken)
            params.set('id_token_hint', idToken);
        window.location.href = `${logoutUrl}?${params.toString()}`;
    }, [keycloakUrl]);
    const value = {
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        getAccessToken,
        refreshProfile,
    };
    return _jsx(AuthContext.Provider, { value: value, children: children });
}
export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
//# sourceMappingURL=AuthProvider.js.map